function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import endpoints from "../../endpoints";
import { call, toast } from "../audiences/api";
import { playAudio, stopAudio, playingKey, subscribePlaying } from "./voices";

/*
 * Upload your own track (DS 2026-10-11: no hosted music library; keep the paste-a-link field and add upload-your-own).
 * Used under the music link field in the sequence editor's Sound tab and the clip editor's Cut and caption panel.
 *
 * A track is a content asset: POST /content/assets/upload-url (mp3, m4a or wav, 50 MB) → PUT to S3 → POST /content/assets
 * { kind: "audio", url, title, source: "upload", durationSeconds, rightsConfirmed: true, rightsStatement }. The server
 * refuses an audio asset without rightsConfirmed and stamps rights = 'confirmed', rights_note = {statement, by, at} and
 * created_by. GET /content/music/tracks lists them ("Your tracks"). Picking one hands its public URL to onChange, so the
 * renderers get it exactly like a pasted link.
 */

const ROOT = String(endpoints.product && endpoints.product.root || "");
const CONTENT = `${ROOT}/content`;
export const MAX_TRACK_BYTES = 50 * 1024 * 1024;
export const RIGHTS_STATEMENT = "I have the rights to use this music";
const TYPES = {
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav"
};
const extOf = name => ((/\.([a-z0-9]+)$/i.exec(name || "") || [])[1] || "").toLowerCase();
const fmtDur = s => s > 0 ? `${Math.floor(s / 60)}:${String(Math.round(s) % 60).padStart(2, "0")}` : "";
const fmtMb = b => `${(b / (1024 * 1024)).toFixed(b < 10 * 1024 * 1024 ? 1 : 0)} MB`;
const fmtDay = iso => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};
let _tracks = null; // module cache: one load per session, shared by both editors
const _subs = new Set();
const emit = () => _subs.forEach(fn => fn(_tracks));
export async function loadTracks(force) {
  if (_tracks && !force) return _tracks;
  try {
    const d = await call(`${CONTENT}/music/tracks`);
    _tracks = (d && d.items || []).map(t => ({
      ...t,
      id: String(t.id)
    }));
  } catch (e) {
    _tracks = _tracks || [];
  }
  emit();
  return _tracks;
}
function audioDuration(file) {
  return new Promise(res => {
    try {
      const a = document.createElement("audio");
      a.preload = "metadata";
      const u = URL.createObjectURL(file);
      const done = v => {
        URL.revokeObjectURL(u);
        res(v);
      };
      let settled = false;
      const once = v => {
        if (!settled) {
          settled = true;
          done(v);
        }
      };
      a.onloadedmetadata = () => once(isFinite(a.duration) ? Math.round(a.duration) : null);
      a.onerror = () => once(null);
      setTimeout(() => once(null), 4000); // a hidden tab defers media loading; never hold the upload on it
      a.src = u;
    } catch (e) {
      res(null);
    }
  });
}

/** Upload one track and register it as an audio asset with the rights confirmation. Resolves the track row. */
export async function uploadTrack(file, title, onProgress) {
  const ext = extOf(file.name);
  if (!TYPES[ext]) throw new Error("Use an mp3, m4a or wav file.");
  if (file.size > MAX_TRACK_BYTES) throw new Error("Music tracks can be up to 50 MB.");
  const contentType = TYPES[ext];
  const pre = await call(`${CONTENT}/assets/upload-url`, {
    method: "POST",
    body: {
      fileName: file.name,
      contentType,
      bytes: file.size
    }
  });
  if (!pre || !pre.uploadUrl) throw new Error(pre && pre.error || "Could not prepare the upload.");
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", pre.uploadUrl, true);
    xhr.setRequestHeader("content-type", pre.contentType || contentType);
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100));
    };
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(file);
  });
  const durationSeconds = await audioDuration(file);
  const name = (title || "").trim() || file.name.replace(/\.[a-z0-9]+$/i, "");
  const a = await call(`${CONTENT}/assets`, {
    method: "POST",
    body: {
      kind: "audio",
      url: pre.url,
      title: name,
      source: "upload",
      durationSeconds,
      rightsConfirmed: true,
      rightsStatement: RIGHTS_STATEMENT
    }
  });
  const track = {
    id: String(a && (a.id || a.Id) || ""),
    title: name,
    url: pre.url,
    durationSeconds,
    rightsConfirmed: true,
    createdDate: new Date().toISOString()
  };
  _tracks = [track, ...(_tracks || []).filter(t => t.url !== track.url)];
  emit();
  loadTracks(true); // pick up who/when from the server
  return track;
}

/**
 * The upload button, the upload form (name, rights checkbox) and "Your tracks". `value` is the music URL the editor
 * holds (the same field the link input writes); `onChange(url)` sets it.
 */
export class MusicTracks extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      tracks: _tracks,
      file: null,
      name: "",
      rights: false,
      busy: false,
      pct: 0,
      error: "",
      playing: playingKey(),
      all: false
    });
    _defineProperty(this, "fileRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "pick", e => {
      const f = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!f) return;
      const ext = extOf(f.name);
      if (!TYPES[ext]) {
        this.setState({
          file: null,
          error: "Use an mp3, m4a or wav file."
        });
        return;
      }
      if (f.size > MAX_TRACK_BYTES) {
        this.setState({
          file: null,
          error: `That file is ${fmtMb(f.size)}. Music tracks can be up to 50 MB.`
        });
        return;
      }
      this.setState({
        file: f,
        name: f.name.replace(/\.[a-z0-9]+$/i, ""),
        rights: false,
        error: "",
        pct: 0
      });
    });
    _defineProperty(this, "upload", async () => {
      const {
        file,
        name,
        rights
      } = this.state;
      if (!file || !rights) return;
      this.setState({
        busy: true,
        error: "",
        pct: 0
      });
      try {
        const t = await uploadTrack(file, name, pct => this._alive && this.setState({
          pct
        }));
        if (!this._alive) return;
        this.setState({
          file: null,
          name: "",
          rights: false,
          busy: false,
          pct: 0
        });
        this.props.onChange(t.url);
        toast(`"${t.title}" uploaded and set as the music.`);
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          error: e && e.message || "The upload did not finish."
        });
      }
    });
  }
  componentDidMount() {
    this._alive = true;
    this._sub = t => {
      if (this._alive) this.setState({
        tracks: t
      });
    };
    _subs.add(this._sub);
    this._unsubPlay = subscribePlaying(k => {
      if (this._alive) this.setState({
        playing: k
      });
    });
    loadTracks();
  }
  componentWillUnmount() {
    this._alive = false;
    _subs.delete(this._sub);
    if (this._unsubPlay) this._unsubPlay();
  }
  render() {
    const {
      value,
      disabled,
      onChange
    } = this.props;
    const {
      tracks,
      file,
      name,
      rights,
      busy,
      pct,
      error,
      playing,
      all
    } = this.state;
    const list = tracks || [];
    const cur = (value || "").trim();
    const SHORT = 4;
    let shown = all ? list : list.slice(0, SHORT);
    const sel = list.find(t => t.url === cur);
    if (!all && sel && !shown.includes(sel)) shown = [...shown.slice(0, SHORT - 1), sel];
    return /*#__PURE__*/React.createElement("div", {
      className: "mt"
    }, /*#__PURE__*/React.createElement("input", {
      ref: this.fileRef,
      type: "file",
      accept: ".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav",
      hidden: true,
      onChange: this.pick
    }), file ? /*#__PURE__*/React.createElement("div", {
      className: "mt-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "mt-file"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "music_note"), /*#__PURE__*/React.createElement("span", {
      className: "n",
      title: file.name
    }, file.name), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, fmtMb(file.size))), /*#__PURE__*/React.createElement("label", {
      className: "mt-name"
    }, "Track name", /*#__PURE__*/React.createElement("input", {
      value: name,
      maxLength: 120,
      disabled: busy,
      onChange: e => this.setState({
        name: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "mt-rights"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: rights,
      disabled: busy,
      onChange: e => this.setState({
        rights: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", null, RIGHTS_STATEMENT)), /*#__PURE__*/React.createElement("div", {
      className: "mt-row"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "mt-btn primary",
      disabled: busy || !rights || disabled,
      title: !rights ? "Tick the box to confirm you have the rights first" : undefined,
      onClick: this.upload
    }, busy ? `Uploading ${pct}%` : "Upload"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "mt-btn",
      disabled: busy,
      onClick: () => this.setState({
        file: null,
        error: ""
      })
    }, "Cancel"))) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "mt-btn dashed",
      disabled: disabled,
      onClick: () => this.fileRef.current && this.fileRef.current.click()
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "upload"), "Upload your own track"), !file ? /*#__PURE__*/React.createElement("span", {
      className: "mt-hint"
    }, "mp3, m4a or wav, up to 50 MB") : null, error ? /*#__PURE__*/React.createElement("span", {
      className: "mt-err",
      role: "alert"
    }, error) : null, list.length ? /*#__PURE__*/React.createElement("div", {
      className: "mt-list",
      role: "list",
      "aria-label": "Your tracks"
    }, /*#__PURE__*/React.createElement("span", {
      className: "mt-h"
    }, "Your tracks"), shown.map(t => {
      const key = `track:${t.url}`;
      const isP = playing === key;
      const on = t.url === cur;
      const who = t.rightsBy ? `Rights confirmed by ${t.rightsBy}${t.rightsAt ? ` on ${fmtDay(t.rightsAt)}` : ""}` : "Rights confirmed at upload";
      return /*#__PURE__*/React.createElement("div", {
        key: t.id || t.url,
        role: "listitem",
        className: "mt-track" + (on ? " on" : "")
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "mt-play",
        "aria-label": isP ? `Stop ${t.title}` : `Play ${t.title}`,
        title: isP ? "Stop" : "Play",
        onClick: () => isP ? stopAudio() : playAudio(key, t.url)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, isP ? "stop" : "play_arrow")), /*#__PURE__*/React.createElement("span", {
        className: "mt-t",
        title: who
      }, /*#__PURE__*/React.createElement("b", null, t.title || "Untitled track"), /*#__PURE__*/React.createElement("i", null, [fmtDur(Number(t.durationSeconds) || 0), on ? "In use" : ""].filter(Boolean).join(" · ") || "Track")), on ? /*#__PURE__*/React.createElement("span", {
        className: "mt-in"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "check")) : /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "mt-btn sm",
        disabled: disabled,
        onClick: () => onChange(t.url)
      }, "Use"));
    }), list.length > SHORT ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "mt-link",
      onClick: () => this.setState({
        all: !all
      })
    }, all ? "Fewer tracks" : `All ${list.length} tracks`) : null) : null);
  }
}